import { get } from 'lodash-es'
import { useEffect } from 'react'
import { toast } from 'react-toastify'

import { Select } from '@loadsmart/loadsmart-ui'
import { Field, Layout } from '@loadsmart/miranda-react'

import { type Option } from 'core/domain/Option'
import { RequestStatus } from 'core/domain/Request'
import { intSizes } from 'core/miranda-styles/screen'
import useWindowWidth from 'core/ui/hooks/useWindowWidth'

import { useConfigContext } from 'config/ui/ConfigContext'

import { type AgnosticShipmentFilter } from '../domain/AgnosticShipmentFilter'
import { useGetShipmentsMetadata } from './hooks/useGetShipmentsMetadata'
import { ShipmentsMetadataItem } from './ShipmentsMetadataItem'
import { type SearchFilters } from './ShipperLoadsPage'

type ShipmentsMetadataProps = {
  filters: AgnosticShipmentFilter
  onClick: (filters: SearchFilters | undefined) => void
  shouldRenderIfMobile?: boolean
}

type FilterKey = 'pendingAppointments' | 'missingPods' | 'missingInvoices'

const FILTERS = [
  {
    label: 'Pending appointments',
    value: 'pendingAppointments',
  },
  {
    label: 'Missing pods',
    value: 'missingPods',
  },
  {
    label: 'Missing invoices',
    value: 'missingInvoices',
  },
]

const FILTER_OPTIONS: Array<Option> = FILTERS.map(filter => ({
  label: filter.label,
  value: filter.value,
}))

export function ShipmentsMetadata({
  filters,
  onClick,
  shouldRenderIfMobile,
}: ShipmentsMetadataProps) {
  const windowWidth = useWindowWidth()
  const isMobile = windowWidth <= intSizes.sm

  const configs = useConfigContext()
  const isConfigsLoaded = configs.configStatus.isDone
  const enableNewShipmentsEndpoint = configs.enableNewShipmentsEndpoint.toBoolean()
  const enableShipmentsMetadata = configs.enableShipmentsMetadata.toBoolean()

  const { shipmentsMetadata, status, fetchShipmentsMetadata } = useGetShipmentsMetadata()

  useEffect(() => {
    if (isConfigsLoaded) {
      fetchShipmentsMetadata(enableNewShipmentsEndpoint)
    }
  }, [enableNewShipmentsEndpoint, fetchShipmentsMetadata, isConfigsLoaded])

  useEffect(() => {
    if (status === RequestStatus.Error) {
      toast.error('Failed to fetch shipments metadata')
    }
  }, [status])

  function handleFilterClick(filterKey?: FilterKey) {
    if (!filterKey) {
      onClick({
        pendingAppointments: undefined,
        missingPods: undefined,
        missingInvoices: undefined,
      })
      return
    }

    const isActive = filters[filterKey]

    if (isActive) {
      onClick({ [filterKey]: undefined })
    } else {
      const allFilters: Record<FilterKey, boolean | undefined> = {
        pendingAppointments: undefined,
        missingPods: undefined,
        missingInvoices: undefined,
      }
      allFilters[filterKey] = true
      onClick(allFilters)
    }
  }

  if (!enableShipmentsMetadata) {
    return null
  }

  const filterKeys: Array<FilterKey> = ['pendingAppointments', 'missingPods', 'missingInvoices']
  const selectedFilter = filterKeys.find(key => filters[key])
  const renderMobile = isMobile && shouldRenderIfMobile
  const renderDesktop = !isMobile && !shouldRenderIfMobile

  if (renderMobile) {
    return (
      <Field>
        <Field.Label id="shipments-metadata-label">Saved filters </Field.Label>
        <Select
          name="shipments-metadata"
          placeholder="Saved filters"
          value={FILTER_OPTIONS.find(option => option.value === selectedFilter)}
          onChange={event => {
            const value = get(event, 'target.value.value')
            handleFilterClick(value)
          }}
          options={[
            {
              label: 'Pending appointments',
              value: 'pendingAppointments',
            },
            {
              label: 'Missing pods',
              value: 'missingPods',
            },
            {
              label: 'Missing invoices',
              value: 'missingInvoices',
            },
          ]}
        />
      </Field>
    )
  }

  const totalShipments = Number(shipmentsMetadata?.total ?? 0)

  if (renderDesktop) {
    return (
      <Layout.Switcher
        paddingT="spacing-2"
        paddingB="spacing-4"
        gap="spacing-4"
        threshold="200px"
        limit={3}
      >
        <ShipmentsMetadataItem
          active={filters.pendingAppointments}
          title="Pending appointments"
          value={shipmentsMetadata?.pendingAppointments ?? 0}
          totalShipments={totalShipments}
          onClick={() => handleFilterClick('pendingAppointments')}
        />
        <ShipmentsMetadataItem
          active={filters.missingPods}
          title="Missing pods"
          value={shipmentsMetadata?.missingPods ?? 0}
          totalShipments={totalShipments}
          onClick={() => handleFilterClick('missingPods')}
        />
        <ShipmentsMetadataItem
          active={filters.missingInvoices}
          title="Missing invoices"
          value={shipmentsMetadata?.missingInvoices ?? 0}
          totalShipments={totalShipments}
          onClick={() => handleFilterClick('missingInvoices')}
        />
      </Layout.Switcher>
    )
  }

  return null
}
